import React, { useEffect, useState, useContext } from 'react'
import { FormattedMessage } from 'react-intl'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import Tabs from '../tabs'
import Button from 'components/Button'
import Modal from 'components/Modal'
import UserAddIcon from 'assets/icons/user-add'
import { requestGetSiteCoProposers } from 'api/site'
import siteStatuses, { SITE_STATUS_DRAFT_ID, SITE_STATUS_PROPOSED_ID, SITE_STATUS_EDIT_ONLY_ID, SITE_STATUS_SUBMITTED_EDIT_ONLY_ID } from 'constants/siteStatuses'
import IconsRow from 'components/IconsRow'
import ManageReviewersModal from './components/manageReviewersModal'
import { requestGetSiteReviewers } from 'api/siteReviewer'
import { SiteContext } from 'providers/site'
import ClearIcon from 'assets/icons/clear-icon16px'
import InfoIcon16 from 'assets/icons/info-icon16px'
import DropdownIndicatorIconOpen from 'assets/icons/dropdown-indicator-opened24px'
import DropdownIndicatorIconClosed from 'assets/icons/dropdown-indicator-closed24px'
import Tooltip from 'components/Tooltip'
import { canReviewSites, getProfileRoleId } from 'utils/rolePermissions'
import { ProfileContext } from 'providers/profile'

import ManageUserIcon from 'assets/icons/manage-users'
import ManageCoProposerModal from '../../pages/about/components/manageCoProposersModal'

import {
  NewSiteHeader, NewSiteHeaderDropdownIconWrapper, NewSiteHeaderDropdownMenu,
  NewSiteHeaderManageUserWrapper, NewSiteHeaderNotification,
  NewSiteHeaderSideSpacer,
  NewSiteHeaderStatusIcon, NewSiteHeaderStatusWrapper,
  NewSiteHeaderTitle,
  NewSiteHeaderTitleWrapper, NewSiteHeaderTooltip,
  NewSiteHeaderUsersIcons
} from './style'
import ProfileIcon from 'components/ProfileIcon'
import {
  buildAboutUrl,
  buildMapUrl,
  buildDetailsUrl,
  buildAssessmentUrl,
  buildMonitoringUrl,
  buildCalculationsUrl,
  editOnly
} from 'routing/constants'
import { HeaderNotificationCalendarDate, HeaderNotificationCalendarText } from '../../pages/about/style'


const statusColor = {
  1: 'rgba(225, 25, 0, 1)',
  2: 'rgba(10, 161, 244, 1)',
  3: 'rgb(255, 153, 0)',
  4: 'rgba(146, 191, 0, 1)',
  5: 'rgba(5, 148, 79, 1)',
  6: 'rgb(239, 85, 137)',
  7: 'rgba(225, 25, 0, 1)',
  8: 'rgba(32, 32, 32, 1)',
  9: 'rgba(103, 58, 183, 1)',
  10: 'rgba(30, 144, 255, 0.6)'
}

const notificationText = {
  1: 'draftSiteMessage',
  2: 'proposedSiteMessage',
  3: 'nominatedSiteMessage',
  4: 'reviewedByRFP',
  5: 'confirmed ',
  6: 'superseded ',
  7: 'delisted',
  8: 'Does not Qualify',
  9: 'Edit only',
  10: 'Edits submitted'
}

const statusHint = {
  1: 'draftSiteStatusHint',
  2: 'proposedSiteStatusHint',
  3: 'nominatedSiteStatusHint',
  4: 'reviewedSiteStatusHint',
  5: 'confirmedSiteStatusHint',
  6: 'supersededSiteStatusHint',
  7: 'delistedSiteStatusHint',
  8: 'doesNotQualifyHint',
  9: 'editSiteStatusHint',
  10: 'confirmedEditOnlyHint'
}

const tooltipXOffset = 0
const tooltipYOffset = 16
const dateOptions = { day: 'numeric', year: 'numeric', month: 'short' }
const Header = () => {
  const params = useParams()
  const history = useNavigate()
  const location = useLocation()
  const { site, isEditable } = useContext(SiteContext)
  const { profile } = useContext(ProfileContext)
  const profileRoleId = getProfileRoleId(profile)
  const canInviteReviewers = canReviewSites(profileRoleId)
  const dateToShow = new Date(site.updatedAt)

  const pathnameSplit = location.pathname.split('/')
  const lastPieceOfPathname = pathnameSplit.at(-1)

  const [siteCoProposers, setSiteCoProposers] = useState([])
  const [siteReviewers, setSiteReviewers] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(lastPieceOfPathname)
  const [isNotificationPresent, setIsNotificationPresent] = useState(true)
  const [isDropDownOpen, setIsDropDownOpen] = useState(false)

  const editOnlySiteStatus = site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID || site.SiteStatusId === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
  const editOnlySite = editOnlySiteStatus ? editOnly : null
  // Prefer route params for parent; prefer loaded edit-only PK over a stale/wrong URL segment
  // (older links put sitehistoryId in :siteHistoryId).
  const routeSiteId = params.id
  const historyId = editOnlySiteStatus
    ? (site.id || params.siteHistoryId)
    : params.siteHistoryId

  const hideNotification = () => {
    setIsNotificationPresent(false)
  }

  function getSiteCoProposers() {
    requestGetSiteCoProposers(params.id, params.siteHistoryId).then((data) => {
      const result = params.siteHistoryId ? data : data.siteCoProposers
      setSiteCoProposers(result)
    })
  }

  async function getSiteReviewers() {
    const data = await requestGetSiteReviewers(site.id, historyId, editOnlySite)
    setSiteReviewers(data)
  }


  useEffect(() => {
    const pathnameSplitOnChange = location.pathname.split('/')
    const lastPieceOfPathnameOnChange = pathnameSplitOnChange.at(-1)
    setActiveTab(lastPieceOfPathnameOnChange)
  }, [location.pathname])

  useEffect(() => {
    if (!site?.SiteStatusId) return;

    if (site.SiteStatusId === SITE_STATUS_DRAFT_ID) {
      getSiteCoProposers();
    } else if (site.SiteStatusId !== SITE_STATUS_EDIT_ONLY_ID) {
      getSiteReviewers();
    }
  }, [site]);

  function changeActiveTab(e) {
    const tabSiteId = editOnlySiteStatus ? routeSiteId : site.id
    const path = {
      about: () => {
        return history(buildAboutUrl(tabSiteId, historyId, editOnlySite))
      },
      map: () => {
        return history(buildMapUrl(tabSiteId, historyId, editOnlySite))
      },
      details: () => {
        return history(buildDetailsUrl(tabSiteId, historyId, editOnlySite))
      },
      assessment: () => {
        return history(buildAssessmentUrl(tabSiteId, historyId, editOnlySite))
      },
      monitoring: () => {
        history(buildMonitoringUrl(tabSiteId, historyId, editOnlySite))
      },
      'kba-criterias': () => {
        history(buildCalculationsUrl(tabSiteId, historyId, editOnlySite))
      }
    }
    setActiveTab(e.target.id)
    path[e.target.id]()
  }

  function toggleModal() {
    setIsModalOpen((s) => {
      return !s
    })
    if (site.SiteStatusId === SITE_STATUS_DRAFT_ID) {
      return getSiteCoProposers()
    }
    getSiteReviewers()
  }

  function openReviewersDropdown() {
    if (siteReviewers.length === 0) {
      return
    }
    setIsDropDownOpen((s) => {
      return !s
    })
  }

  const siteStatus = siteStatuses.find(status => status.id === site.SiteStatusId)

  const renderReviewersList = siteReviewers.map((reviewer) => (
    <ProfileIcon key={reviewer.id} user={reviewer.User} reviewer={true} />
  ))

  const isReviewed = siteStatus.name === 'reviewed by rfp'

  return (
    <>
      <NewSiteHeader>
        <NewSiteHeaderTitleWrapper>
          <NewSiteHeaderTitle data-test='page-header'><FormattedMessage id='assessment' /></NewSiteHeaderTitle>
          <NewSiteHeaderStatusIcon data-test='site-status' color={statusColor[site.SiteStatusId]} />
          <NewSiteHeaderStatusWrapper>
            {siteStatus?.name && <FormattedMessage id={isReviewed ? 'Reviewed' : siteStatus.name} />}
          </NewSiteHeaderStatusWrapper>
          <Tooltip
            style={{ zIndex: 1000 }}
            placement='bottom'
            trigger={['hover']}
            align={{ offset: [tooltipXOffset, tooltipYOffset] }}
            overlay={{ label: statusHint[site.SiteStatusId] }}
          >
            <NewSiteHeaderTooltip>
              <InfoIcon16 />
            </NewSiteHeaderTooltip>
          </Tooltip>
        </NewSiteHeaderTitleWrapper>
        <Tabs changeTab={changeActiveTab} active={activeTab} />
        {site.SiteStatusId === SITE_STATUS_DRAFT_ID && (
          <NewSiteHeaderManageUserWrapper>
            <NewSiteHeaderUsersIcons width='166px'>
              <IconsRow usersList={siteCoProposers} labelText='coProposers' />
            </NewSiteHeaderUsersIcons>
            <Button isDisabled={!isEditable} onClick={toggleModal} height='40px' withIcon={true} marginTop='0' dataTest='invite'
              type={isEditable ? 'secondary' : 'disabledPrimary'}>
              {siteCoProposers.length ? <ManageUserIcon /> : <UserAddIcon />}
              <FormattedMessage id={siteCoProposers.length ? 'manage' : 'invite'} />
            </Button>
          </NewSiteHeaderManageUserWrapper>
        )}
        {site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID && (
          <NewSiteHeaderSideSpacer aria-hidden="true">
            <NewSiteHeaderUsersIcons width='166px' />
            <div style={{ width: '108px', height: '40px' }} />
          </NewSiteHeaderSideSpacer>
        )}
        {site.SiteStatusId === SITE_STATUS_PROPOSED_ID && (
          <NewSiteHeaderManageUserWrapper>
            <NewSiteHeaderUsersIcons width='144px'>
              <IconsRow usersList={siteReviewers} labelText='reviewers' />
            </NewSiteHeaderUsersIcons>
            <Button isDisabled={!canInviteReviewers} onClick={toggleModal} height='40px' withIcon={true} marginTop='0' dataTest='invite'
              type={canInviteReviewers ? 'secondary' : 'disabledPrimary'}>
              {siteReviewers.length ? <ManageUserIcon /> : <UserAddIcon />}
              <FormattedMessage id={siteReviewers.length ? 'manage' : 'invite'} />
            </Button>
          </NewSiteHeaderManageUserWrapper>

        )}
        {(site.SiteStatusId !== SITE_STATUS_PROPOSED_ID &&
          site.SiteStatusId !== SITE_STATUS_DRAFT_ID &&
          site.SiteStatusId !== SITE_STATUS_EDIT_ONLY_ID) && (

          <NewSiteHeaderManageUserWrapper>
            <NewSiteHeaderUsersIcons width='144px'>
              <IconsRow usersList={siteReviewers} labelText='reviewers' />
            </NewSiteHeaderUsersIcons>
            <NewSiteHeaderDropdownIconWrapper isActive={isDropDownOpen}>
              <div onClick={openReviewersDropdown}>
                {isDropDownOpen ? <DropdownIndicatorIconOpen /> : <DropdownIndicatorIconClosed />}
              </div>
              <NewSiteHeaderDropdownMenu isActive={isDropDownOpen && siteReviewers.length > 0} className='scroll'>
                {renderReviewersList}
              </NewSiteHeaderDropdownMenu>
            </NewSiteHeaderDropdownIconWrapper>
          </NewSiteHeaderManageUserWrapper>
        )}
      </NewSiteHeader>
      {isNotificationPresent && (
        <NewSiteHeaderNotification>
          <div />
          {(site.reassessmentFlag && site.SiteStatusId === SITE_STATUS_DRAFT_ID) ? (
            <span>
              <HeaderNotificationCalendarText>
                Site last time reassessed on
              </HeaderNotificationCalendarText>
              <span>
                —
              </span>
              <HeaderNotificationCalendarDate>
                {dateToShow.toLocaleDateString('en-US', dateOptions)}
              </HeaderNotificationCalendarDate>
            </span>
          ) : (
            <FormattedMessage id={notificationText[site.SiteStatusId]} />
          )}
          <div onClick={hideNotification}>
            <ClearIcon />
          </div>
        </NewSiteHeaderNotification>
      )}
      <Modal
        width={'640px'}
        isOpen={isModalOpen && site.SiteStatusId === SITE_STATUS_DRAFT_ID}
        toggleModal={toggleModal}
        modalTitleId='inviteCoProposersModalTitle'
      >
        <ManageCoProposerModal toggleModal={toggleModal} />
      </Modal>
      <Modal
        width='640px'
        isOpen={isModalOpen && site.SiteStatusId === SITE_STATUS_PROPOSED_ID}
        toggleModal={toggleModal}
        modalTitleId='inviteReviewers'
      >
        <ManageReviewersModal toggleModal={toggleModal} />
      </Modal>
    </>
  )
}

export default Header
